import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useDispatch } from 'react-redux'

import { useQuery } from '@tanstack/react-query'
import { FormHandles } from '@unform/core'
import { Alert } from '@vtex/shoreline'

import Table from '~/components/Table'
import { Badge } from '~/components/UI/Badge'
import { FileInput } from '~/components/unform'
import { getWhiteLabelPublisherId } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import OffsiteCreativesList from '~/modules/retailMedia/components/CampaignForm/elements/OffsiteCreativesList'
import { CampaignChannel } from '~/modules/retailMedia/constants/campaignChannel'
import { listSizesBySegmentationService } from '~/modules/retailMedia/services/placements/sizes'
import { getPublisherConfiguration } from '~/modules/retailMedia/store/industry/publisherConfiguration/actions'

import { publisherWithMaxDimensions } from '../../hooks/constants/publishersWithMaxDimensions'
import { useCampaignForm } from '../../hooks/useCampaignForm'
import FileToUpload from '../FileToUpload'
import { destinationUrlColumn, schema } from './schema'
import { schemaVideo } from './schemaVideo'
import SizeGuide from './SizeGuide'

import * as S from './styles'

// Own-context size limit (FR-020, FR-021e): 500 KB per file, applied only
// when the offsite channel is selected — never derived from the video
// component's constant, and never applied to the current/onsite flow.
const OFFSITE_CHANNEL_MAX_IMAGE_FILE_SIZE_KB = 500
const OFFSITE_CHANNEL_MAX_IMAGE_FILE_SIZE_BYTES =
  OFFSITE_CHANNEL_MAX_IMAGE_FILE_SIZE_KB * 1024

// O pedido do parceiro aceita 20 arquivos; um lugar é do CSV de audiência, então
// sobram 19 para os criativos (FR-053i). Antes disso não havia máximo nenhum, e o
// excedente só seria recusado no despacho, com o briefing inteiro já preenchido.
const OFFSITE_MAX_CREATIVES = 19

export const BannerAddFormContent = ({
  formRef,
  bannersToTable,
  setBannersToTable,
  initialData,
  selectedSegmentation,
  type
}: BannerAddFormContentProps<FormHandles>) => {
  const { t } = useAPPTranslation()
  const { selectedSubpublishers, allowOffsiteBriefing, selectedChannel } =
    useCampaignForm()

  // Needs BOTH the ad type AND the briefing permission (FR-004e, FR-020,
  // FR-017k) — neither alone is enough. Permission alone isn't enough:
  // `allowOffsiteBriefing` also applies to Onsite Banner/Video, which reuse
  // this same BannerAddFormContent, so without the type check the 500 KB
  // limit, the size guide and the per-creative URL field would leak into
  // media that was never part of the offsite flow. Ad type alone isn't
  // enough either: `banner_off_site` also covers a publisher without the
  // permission (Globo/Buscapé/TikTok), and that flow must stay
  // byte-for-byte unchanged (FR-004f). Deliberately NOT keyed on the
  // channel being chosen (dv360/meta) — same composition as the rest of
  // the new flow (FR-017k, FR-017l): nothing here is channel-specific, so
  // waiting for a channel on top of the permission only opened a window
  // where media accepted before the channel was picked skipped the 500 KB
  // limit and had no destination-URL field to fill.
  const isOffsiteBriefingActive =
    type === 'banner_off_site' && allowOffsiteBriefing

  /**
   * Lógica de apoio
   */
  const { publisherId } = useMemo(
    () => ({
      publisherId: initialData?.publisher_id
    }),
    [initialData]
  )

  /**
   * Lógica para a funcionalidade principal
   */

  const [filesToUpload, setFilesToUpload] = useState<FilesToUpload>([])
  // Só precisa ser único entre lotes que convivem na fila (abaixo); um contador
  // monotônico evita depender de crypto.randomUUID, ausente no ambiente de teste
  // (jsdom) e em navegadores mais antigos.
  const nextFileIdRef = useRef(0)

  const dispatch = useDispatch()

  useEffect(() => {
    if (publisherId) {
      dispatch(getPublisherConfiguration(publisherId))
    }
  }, [dispatch, publisherId])

  const [creativeLimitReached, setCreativeLimitReached] = useState(false)

  const handleDeleteBanner = useCallback(
    (data: { id: string }) => {
      setBannersToTable(state => state.filter(item => item.id !== data.id))
      // Removing a creative always frees a slot, so a stale "selection was
      // cut" signal from a previous over-limit attempt no longer applies --
      // without this, the warning stayed up even after there was room again
      // (achado de revisão, PR #745, 2026-09-28).
      setCreativeLimitReached(false)
    },
    [setBannersToTable]
  )

  // Per-creative destination URL (FR-022g a FR-022i): edited on the
  // already-accepted table row via destinationUrlColumn, not at upload time
  // — see the note on that column for why.
  const handleUpdateDestinationUrl = useCallback(
    (id: string, url: string) => {
      setBannersToTable(state =>
        state.map(item =>
          item.id === id ? { ...item, destination_url: url || undefined } : item
        )
      )
    },
    [setBannersToTable]
  )

  // Título, descrição e chamada para ação do canal Meta: o mesmo caminho da
  // URL — o valor mora no criativo aceito e segue com ele no envio.
  const handleUpdateCreativeCopy = useCallback(
    (id: string, field: keyof CreativeCopy, value: string) => {
      setBannersToTable(state =>
        state.map(item =>
          item.id === id ? { ...item, [field]: value || undefined } : item
        )
      )
    },
    [setBannersToTable]
  )

  const handleSaveBanner = useCallback(
    (data: ItemBannerTable) => {
      const altText = formRef.current.getFieldValue('banner.alt_text')

      setFilesToUpload(prevState =>
        prevState.filter(item => item.id !== data.id)
      )

      setBannersToTable(prevState => {
        // Última barreira: a fila de envio já é cortada em
        // handlePrepareToUploadImage, mas um arquivo pode chegar aqui por outro
        // caminho (arrastar e soltar, uma segunda seleção concorrente).
        if (
          isOffsiteBriefingActive &&
          prevState.length >= OFFSITE_MAX_CREATIVES
        )
          return prevState

        return [
          ...prevState,
          {
            ...data,
            alt_text: altText,
            handleDeleteBanner,
            handleUpdateDestinationUrl,
            handleUpdateCreativeCopy
          }
        ]
      })
    },
    [
      formRef,
      handleDeleteBanner,
      handleUpdateCreativeCopy,
      handleUpdateDestinationUrl,
      isOffsiteBriefingActive,
      setBannersToTable
    ]
  )

  /**
   * Handle multiple upload
   */

  const handlePrepareToUploadImage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // `id` precisa ser único entre lotes, não só dentro de um: agora que uma
      // segunda seleção offsite é somada à fila em vez de substituí-la (abaixo),
      // um id baseado só no índice colidiria com um arquivo de mesmo nome vindo
      // de um lote anterior ainda pendente.
      // event.target.files is nullable -- a cancelled file picker/a synthetic
      // change event can fire with no files, and Array.from(null, ...) throws
      // (achado de revisão, 2026-09-25).
      const fileList = Array.from(event.target.files ?? [], item => ({
        file: item,
        id: `${item.name}_${nextFileIdRef.current++}`
      }))

      if (!isOffsiteBriefingActive) {
        setFilesToUpload(fileList)
        return
      }

      // Conta o que já foi aceito e o que ainda está subindo: uma segunda seleção
      // enquanto a primeira leva ainda não foi promovida a `bannersToTable`
      // superestimaria o espaço livre e deixaria o excesso ser descartado
      // silenciosamente pela barreira de handleSaveBanner (achado de revisão,
      // 2026-09-25).
      const remaining =
        OFFSITE_MAX_CREATIVES - bannersToTable.length - filesToUpload.length

      setCreativeLimitReached(fileList.length > remaining)
      // Soma à fila existente em vez de substituí-la: uma segunda seleção
      // enquanto a primeira ainda está em filesToUpload não pode descartar os
      // arquivos que já estavam contados no cálculo de remaining acima (achado
      // de revisão, 2026-09-25).
      setFilesToUpload(prevState => [
        ...prevState,
        ...fileList.slice(0, Math.max(0, remaining))
      ])
    },
    [bannersToTable.length, filesToUpload.length, isOffsiteBriefingActive]
  )

  const handleDeleteFileBeforeUpload = (removeId: string) => {
    setFilesToUpload(prevState =>
      prevState.filter(item => item.id !== removeId)
    )
  }

  /**
   * Ad sizes
   */

  const { data: dataAdSize } = useQuery({
    queryKey: [
      'get-media-size',
      type,
      publisherId,
      selectedSegmentation,
      selectedSubpublishers
    ],
    queryFn: async () => {
      const res = await listSizesBySegmentationService({
        publisher_id: publisherId,
        type,
        segmentation: selectedSegmentation,
        subpublishers: selectedSubpublishers
      })

      return res.data
    }
  })

  const adSizeWithMaxSizes: ResponseListSizeBySegmentation = useMemo(() => {
    if (
      !publisherWithMaxDimensions.includes(
        publisherId || getWhiteLabelPublisherId()
      )
    )
      return dataAdSize

    const newSizes = dataAdSize?.map(item => ({
      ...item,
      max_width: item.width * 4,
      max_height: item.height * 4
    }))

    return newSizes
  }, [dataAdSize, publisherId])

  /**
   * Placements
   */

  const formatLabel = (adSize: SizeBySegmentation) =>
    adSize?.max_height
      ? `${adSize.size} (${t('rm:recommended')}: ${adSize.max_width}x${adSize.max_height})`
      : adSize.size

  const options = useMemo(() => {
    if (adSizeWithMaxSizes) {
      const arrayValue: OptionsInterface[] = adSizeWithMaxSizes.map(item => ({
        value: item.name,
        label: formatLabel(item),
        width: item.width,
        height: item.height,
        max_height: item?.max_height,
        max_width: item?.max_width
      }))

      arrayValue.sort((a, b) =>
        a.width !== b.width ? a.width - b.width : a.height - b.height
      )

      return arrayValue
    }
  }, [adSizeWithMaxSizes])

  const getPlacementByDimension = useCallback(
    ({ width, height }: Dimension) => {
      const findPlacement = adSizeWithMaxSizes?.find(
        item =>
          [Number(item.width), item.max_width].includes(width) &&
          [Number(item.height), item.max_height].includes(height)
      )

      return findPlacement
    },
    [adSizeWithMaxSizes]
  )

  /**
   * Content data
   */

  // Destination URL column only for the offsite new flow (FR-022g a
  // FR-022i) — inserted between size and actions so it doesn't disturb the
  // existing column order for onsite Banner, which reuses this same
  // `schema` without it.
  const { actions, ...mediaAndSizeSchema } = schema
  const offsiteMediaSchema = {
    ...mediaAndSizeSchema,
    destinationUrl: destinationUrlColumn,
    actions
  }
  const acceptedMediaSchema = isOffsiteBriefingActive
    ? offsiteMediaSchema
    : schema

  // Avisa tanto quando a seleção foi cortada agora quanto quando a campanha já
  // está no teto — nos dois casos a próxima escolha não entra.
  const showCreativeLimitAlert =
    isOffsiteBriefingActive &&
    (creativeLimitReached || bannersToTable.length >= OFFSITE_MAX_CREATIVES)

  return (
    <>
      {isOffsiteBriefingActive && <SizeGuide />}

      {showCreativeLimitAlert && (
        <Alert variant="warning" className="mt-3 mb-2">
          {t('rm:campaign.create.Banner.creativeLimit.reached', {
            max: OFFSITE_MAX_CREATIVES
          })}
        </Alert>
      )}

      <section className="multipleUploadWrapper">
        <FileInput
          name="mediaFile"
          accept={type === 'banner_video' ? 'video/mp4' : 'image/*'}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            handlePrepareToUploadImage(event)
          }
          isMultiple
          hideIcons
        >
          {!isOffsiteBriefingActive && (
            <div className="pt-3 mt-3 border-top border-tertiary">
              <h4>{t('rm:allowedMediaSizes')}</h4>

              <div className="d-flex gap 2 justify-content-center flex-wrap">
                {options?.map(item => (
                  <div key={item.value}>
                    <Badge template="primary">{item.label}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </FileInput>

        <S.UploadQueue>
          {filesToUpload?.map(item => (
            <FileToUpload
              data={item}
              key={item.id}
              getPlacementByDimension={getPlacementByDimension}
              onDelete={() => handleDeleteFileBeforeUpload(item.id)}
              onSave={handleSaveBanner}
              maxFileSizeBytes={
                isOffsiteBriefingActive
                  ? OFFSITE_CHANNEL_MAX_IMAGE_FILE_SIZE_BYTES
                  : undefined
              }
              maxFileSizeLabel={
                isOffsiteBriefingActive
                  ? `${OFFSITE_CHANNEL_MAX_IMAGE_FILE_SIZE_KB} KB`
                  : undefined
              }
            />
          ))}
        </S.UploadQueue>
      </section>

      {/* States the per-creative / campaign-wide URL rule right where the
          creatives land, instead of leaving it to a placeholder inside a
          narrow table column (2026-09-15 demo, SPEC 004 item 5.1). The
          block itself stays below the media on purpose: its required state
          is derived from the accepted media (isDestinationUrlRequired in
          the offsite strategy), so moving it above would flip a panel the
          operator already passed. */}
      {isOffsiteBriefingActive && (
        <Alert variant="informational" className="mt-3 mb-2">
          {t('rm:campaign.create.Banner.destinationUrl.creativesHint')}
        </Alert>
      )}

      {/* Cards instead of the table on the offsite briefing flow only: the
          step needs up to four editable fields per creative, which a table
          row cannot hold without cramping them. Every other campaign —
          onsite banner, onsite video, offsite without the permission —
          keeps the table untouched (SPEC 004 items 4.3 and 7.1). */}
      {isOffsiteBriefingActive ? (
        <OffsiteCreativesList
          creatives={bannersToTable}
          isMetaChannel={selectedChannel === CampaignChannel.META}
        />
      ) : (
        !!bannersToTable.length && (
          <S.WrapperTabler>
            <Table
              schema={
                type === 'banner_video' ? schemaVideo : acceptedMediaSchema
              }
              data={bannersToTable}
            />
          </S.WrapperTabler>
        )
      )}
    </>
  )
}
